import { useId, useRef, useState, type ReactNode } from 'react';
import { Eye, EyeOff, ImageUp, Images, Pencil } from 'lucide-react';
import { MediaPicker } from './MediaPicker';
import type { Json } from '../../../lib/database.types';
import type { EditorAsset, EditorBlock, EditorPage } from '../editor.types';
import { pageMeta, workDate } from '../../pages/pageMetadata';
import { Input, Textarea } from '../../../components/ui';
import { firstPicture } from './pageThumbnails';
import { WorkThumbnail } from './WorkThumbnail';
import { workCover, workCardText } from './workDetails';
import './PagesMenu.css';

/** Small label above a control; muted ink passes contrast on the white sheet. */
const labelClass = 'text-xs font-medium text-hazumu-muted';

/** How a work item appears on cards: the card preview doubles as the cover picker. */
export function WorkDetailsFields({ page, blocks, assetsById, disabled, onChange, onUploadCover }: {
  page: EditorPage;
  blocks: EditorBlock[];
  assetsById?: Map<string, EditorAsset>;
  disabled: boolean;
  onChange: (details: Record<string, Json | undefined>) => void;
  /** Uploads a picture for the cover only (it isn't added to the page). */
  onUploadCover?: (file: File) => Promise<EditorAsset>;
}) {
  const [choosing, setChoosing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const [browsing, setBrowsing] = useState(false);
  const meta = pageMeta(page);
  const picture = workCover(page, firstPicture(blocks));
  const card = workCardText(page);
  const pictures = blocks.filter(block => block.block_type === 'picture' && (block.asset_id || (block.content as {sample?: string}).sample));
  const update = (patch: Record<string, Json>) => onChange({...meta, ...patch});
  const canChoose = pictures.length > 0 || Boolean(onUploadCover);
  const upload = async (file: File) => {
    if (!onUploadCover) return;
    setUploading(true);
    setUploadError('');
    try {
      const asset = await onUploadCover(file);
      update({cover: {assetId: asset.id}});
      setChoosing(false);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Could not upload the image.');
    } finally {
      setUploading(false);
    }
  };
  return <fieldset disabled={disabled} className="space-y-4">
    <div className="space-y-1.5">
      <p className={labelClass}>How it looks on cards</p>
      <div className="flex items-center gap-3 rounded-2xl border border-hazumu-border bg-hazumu-white p-2.5">
        <button type="button" className="relative flex-none rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink disabled:cursor-default" disabled={!canChoose} aria-expanded={choosing} aria-label="Change cover image" onClick={() => setChoosing(!choosing)}>
          <WorkThumbnail picture={picture} asset={picture?.asset_id ? assetsById?.get(picture.asset_id) : undefined} />
          {canChoose && <span className="absolute -right-1.5 -bottom-1.5 grid size-6 place-items-center rounded-full border border-hazumu-border bg-hazumu-white text-hazumu-ink" aria-hidden="true"><Pencil size={12} /></span>}
        </button>
        <div className="min-w-0">
          <p className="font-semibold break-words text-hazumu-ink">{page.title || 'Untitled'}</p>
          {card.details && <p className="text-xs text-hazumu-muted">{card.details}</p>}
          {card.description && <p className="mt-0.5 line-clamp-2 text-sm break-words text-hazumu-muted">{card.description}</p>}
        </div>
      </div>
      {!canChoose && <p className="text-xs text-hazumu-muted">Add an image to your page to choose a cover.</p>}
      {choosing && <div className="relative grid grid-cols-2 gap-2" aria-label="Cover image choices">
        {pictures.map((block, index) => <button key={block.id} type="button" className="flex items-center gap-2 rounded-xl border border-hazumu-border bg-hazumu-white p-2 text-sm text-hazumu-ink transition-colors hover:border-hazumu-terracotta hover:bg-hazumu-blush" onClick={() => {
          update({cover: block.asset_id ? {assetId: block.asset_id} : {sample: (block.content as {sample: string}).sample}});
          setChoosing(false);
        }}><WorkThumbnail picture={block} asset={block.asset_id ? assetsById?.get(block.asset_id) : undefined} /><span>Image {index + 1}</span></button>)}
        {meta.cover && <button type="button" className="rounded-xl border border-dashed border-hazumu-border p-2 text-sm text-hazumu-ink transition-colors hover:bg-hazumu-canvas" onClick={() => {
          update({cover: null});
          setChoosing(false);
        }}>Use first image</button>}
        {onUploadCover && <>
          <button type="button" disabled={uploading} className="flex min-h-14 items-center justify-center gap-2 rounded-xl border border-dashed border-hazumu-border p-2 text-sm text-hazumu-ink transition-colors hover:bg-hazumu-canvas disabled:opacity-60" onClick={() => fileInput.current?.click()}>
            <ImageUp size={16} aria-hidden="true" />
            {uploading ? 'Uploading…' : 'Upload an image'}
          </button>
          {assetsById && assetsById.size > 0 && <button type="button" className="flex min-h-14 items-center justify-center gap-2 rounded-xl border border-dashed border-hazumu-border p-2 text-sm text-hazumu-ink transition-colors hover:bg-hazumu-canvas" onClick={() => setBrowsing(true)}>
            <Images size={16} aria-hidden="true" />
            Choose from your images
          </button>}
          <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp,image/gif" aria-label="Upload a cover image" className="sr-only" tabIndex={-1} onChange={event => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void upload(file);
          }} />
        </>}
      </div>}
      {uploadError && <p role="alert" className="text-xs text-hazumu-danger">{uploadError}</p>}
      {browsing && onUploadCover && <MediaPicker
        title="Choose a cover"
        assets={[...(assetsById?.values() ?? [])]}
        onUpload={onUploadCover}
        onChoose={([asset]) => {
          setBrowsing(false);
          if (asset) {
            update({cover: {assetId: asset.id}});
            setChoosing(false);
          }
        }}
        onClose={() => setBrowsing(false)}
      />}
    </div>
    <div className="grid grid-cols-2 gap-3 max-[420px]:grid-cols-1">
      <CardField label="Date" shown={meta.showDate !== false} onShow={showDate => update({showDate})}>
        {id => <Input id={id} type="date" value={workDate(page)} onChange={e => update({date: e.target.value})} />}
      </CardField>
      <CardField label="Category" shown={meta.showCategory !== false} onShow={showCategory => update({showCategory})}>
        {id => <Input id={id} maxLength={80} placeholder="Brand identity" value={typeof meta.category === 'string' ? meta.category : ''} onChange={e => update({category: e.target.value})} />}
      </CardField>
    </div>
    <label className={`block ${labelClass}`}>
      Short description
      <Textarea className="mt-1.5 block resize-none font-normal" rows={2} maxLength={160} placeholder="One or two lines for cards" value={card.description ?? ''} onChange={e => update({cardDescription: e.target.value})} />
    </label>
  </fieldset>;
}

/** A labelled field with an eye toggle for whether its value shows on cards. */
function CardField({ label, shown, onShow, children }: {
  label: string;
  shown: boolean;
  onShow: (shown: boolean) => void;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  const name = label.toLowerCase();
  return <div className="min-w-0">
    <div className="mb-1 flex items-center justify-between">
      <label htmlFor={id} className={labelClass}>{label}</label>
      <button type="button" aria-pressed={shown} aria-label={`Show ${name} on cards`} title={shown ? `Shown on cards` : `Hidden on cards`} className={`grid size-7 place-items-center rounded-lg transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink ${shown ? 'bg-hazumu-blush text-hazumu-ink' : 'text-hazumu-muted hover:bg-hazumu-canvas'}`} onClick={() => onShow(!shown)}>
        {shown ? <Eye size={15} aria-hidden="true" /> : <EyeOff size={15} aria-hidden="true" />}
      </button>
    </div>
    {children(id)}
  </div>;
}
